<template>
    <div :class="$style.container">
        <div :class="$style.grid">
            <div :class="$style.overlay_blur" />
            <!-- Should be on top for better SEO -->
            <LazyHydration :hydrate="hydrateOnIdle()">
                <div :class="$style.left">
                    <LeftSide :languageLinks="localeData?.languageLinks" />
                </div>
            </LazyHydration>
            <div :class="$style.right">
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <div :class="$style.desktop_header">
                        <DesktopHeader />
                    </div>
                </LazyHydration>
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <div :class="$style.mobile_header">
                        <MobileHeader />
                    </div>
                </LazyHydration>
                <slot />
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <div :class="$style.footer">
                        <Footer :languageLinks="localeData?.languageLinks" />
                    </div>
                </LazyHydration>
            </div>
            <StickyBanner />
        </div>
        <LazyHydration :hydrate="hydrateOnIdle()">
            <ModalCategories />
            <ModalInvite />
            <ModalSurvey />
        </LazyHydration>
    </div>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { hydrateOnIdle, onMounted, onServerPrefetch, watch } from 'vue'

import Footer from '@/components_new/Footer/Footer.vue'
import DesktopHeader from '@/components_new/Headers/DesktopHeader.vue'
import MobileHeader from '@/components_new/Headers/MobileHeader.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import LeftSide from '@/components_new/LeftSide/LeftSide.vue'
import ModalCategories from '@/components_new/ModalCategories/ModalCategories.vue'
import ModalInvite from '@/components_new/ModalItems/components/ModalInvite.vue'
import ModalSurvey from '@/components_new/ModalItems/components/ModalSurvey/ModalSurvey.vue'
import StickyBanner from '@/components_new/StickyBanner/StickyBanner.vue'
import { AvailableLocale } from '@/i18n/helpers'
import { useGeneralData } from '@/query/use-general-data'
import { useGoogleAuth } from '@/store/google-auth-store'
import { useLayoutStore } from '@/store/layout-store'
import { useUserStore } from '@/store/user-store/user-store'

const { localeData } = defineProps<{
    localeData?: {
        languageLinks: Record<AvailableLocale, string>
    }
}>()

const googleAuthStore = useGoogleAuth()
const { initialized } = storeToRefs(googleAuthStore)
const { isUserInfoLoaded } = storeToRefs(useUserStore())
const { fullScreen } = storeToRefs(useLayoutStore())
const { suspense } = useGeneralData()

function toggleFullScreen(value: boolean) {
    document.documentElement.classList.toggle('fullscreen_css', value)
}

function setPrompt() {
    if (initialized.value && isUserInfoLoaded.value) {
        googleAuthStore.setPrompt()
    }
}

function togglePrompt(value: boolean) {
    if (value) {
        googleAuthStore.cancelPrompt()
    } else {
        setPrompt()
    }
}

// as we have to re-render Layout for some pages we need to re-toggle fullscreen and all things regarding this - watch not working in this case
function updateLayout() {
    toggleFullScreen(fullScreen.value)
    togglePrompt(fullScreen.value)
}

watch(fullScreen, updateLayout)
watch([initialized, isUserInfoLoaded], setPrompt)
onServerPrefetch(suspense)
onMounted(updateLayout)
</script>
<style module critical>
.desktop_header,
.mobile_header,
.left {
    opacity: 0;
}

:global(body) {
    /* show scroll bar before all the content arrive */
    /* this will be unset once non-critical style arrive (with the content) */
    min-height: 110vh;
}

:global(html.fullscreen_css),
:global(html:has(dialog[open])) {
    overflow: hidden;
    position: relative;
    height: 100%;
    overscroll-behavior: none;
}

:global(html.fullscreen_css body),
:global(html:has(dialog[open]) body) {
    overflow: hidden;
    position: relative;
    height: 100%;
    background: #000;
}

@media (--pwa) {
    :global(html.fullscreen_css),
    :global(html:has(dialog[open])),
    :global(html.fullscreen_css body),
    :global(html:has(dialog[open]) body) {
        height: 100dvh;
    }
}

.container {
    min-height: 100%;
    background-color: #000;
    --global-padding-horizontal: 12px;
    --global-page-top-padding: 12px;
    --global-gap: 12px;
    padding-left: calc(var(--global-padding-horizontal) + env(safe-area-inset-left));
    padding-top: 0;
    padding-right: calc(var(--global-padding-horizontal) + env(safe-area-inset-right));
    padding-bottom: 0;
}

.grid {
    display: grid;
    gap: var(--global-gap);
}

.desktop_header {
    height: 52px;
    margin: var(--global-gap) 0;
    display: block;
    position: relative;
}

.mobile_header {
    display: block;
    margin: 0 0 var(--global-page-top-padding);
}

.left {
    grid-column: 1;
    grid-row: 1;
    z-index: 1;
    max-height: 100vh;
    position: sticky;
    top: 0;
}

.right {
    grid-column: 2;
}

.mobile_header {
    display: block;
}
.desktop_header {
    display: none;
}

@media (--mobile), (--tablet-plus) {
    .container {
        --global-wide-scroller-padding-left: 12px;
        --global-wide-scroller-padding-right: 12px;
        --global-header-pannel-height: 52px;
    }

    .grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .left {
        display: none;
    }

    .right {
        grid-column: 1;
    }

    .desktop_header {
        display: none;
    }

    .mobile_header {
        display: block;
    }
}

@media (--desktop-small-plus) {
    .container {
        --global-wide-scroller-padding-left: 0px;
        --global-wide-scroller-padding-right: 12px;
        --global-right-pannel-width: 300px;
    }

    .grid {
        /* left column is 52px until screen width reaches 1400px, then it is starts growing proportionally */
        grid-template-columns: 52px minmax(0, 1fr);
    }

    .desktop_header {
        display: block;
        /* grid-column: 1 / 3; */
    }
    .mobile_header {
        display: none;
    }

    .left {
        display: block;
    }

    .right {
        grid-column: 2;
    }

    .desktop_header {
        display: block;
    }

    .mobile_header {
        display: none;
    }
}

.overlay_blur {
    position: fixed;
    width: 100vw;
    height: 100vh;
    left: 0;
    top: 0;
    z-index: 1;
    display: none;
    backdrop-filter: blur(5rem) brightness(0.15);
}

:global(html:has(*[data-overlay-blur])) .desktop_header {
    z-index: 1;
}

:global(html:has(*[data-overlay-blur])) .overlay_blur {
    display: block;
}
</style>

<style module>
.desktop_header,
.mobile_header,
.left {
    opacity: 1;
}

:global(body) {
    min-height: unset;
}
</style>
